import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Shield, Check, ExternalLink, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Breadcrumbs from "@/components/Breadcrumbs";

const DeepseekUSA = () => {
  const faqItems = [
    {
      question: "Is DeepSeek available in the United States?",
      answer: "Yes. DeepSeek is fully accessible from all 50 US states — both the free web chat at chat.deepseek.com and the developer API work without a VPN. There is no federal ban, although several states (Texas, New York, Virginia) and federal agencies (US Navy, NASA, Department of Commerce) have restricted DeepSeek on government-issued devices."
    },
    {
      question: "Is DeepSeek safe to use in the US under CCPA?",
      answer: "DeepSeek processes user data on servers in China, which means personal information leaves US jurisdiction. Under the California Consumer Privacy Act (CCPA) and CPRA, California residents retain rights to know, delete and opt out — but enforcing those rights against an overseas processor is difficult. Businesses handling consumer data should obtain explicit consent and avoid pasting customer PII into the chat."
    },
    {
      question: "How much does DeepSeek cost in the US?",
      answer: "The DeepSeek web chat is free for personal use. The API is billed in US dollars: DeepSeek-V4-Flash costs $0.15 per 1M input tokens (cache miss) and $0.60 per 1M output tokens; DeepSeek-V4-Pro costs $0.66 and $1.98. The older deepseek-chat (V3) and deepseek-reasoner (R1) endpoints were switched off on 24 July 2026."
    },
    {
      question: "Can I use DeepSeek for my US business?",
      answer: "Yes — the licence permits commercial use. For regulated industries (HIPAA healthcare, GLBA finance, FedRAMP government workloads) you should either self-host the open-source DeepSeek-V3 weights on a US cloud region (AWS us-east-1, Azure East US) or use a proxy provider with US data residency such as Together AI, Fireworks or DeepInfra."
    },
    {
      question: "Is DeepSeek banned in any US states?",
      answer: "As of 2026, Texas, New York, Virginia and Tennessee have banned DeepSeek on state-government devices. Federal agencies including the US Navy, NASA, Department of Commerce and Department of Defense have similar restrictions. These bans apply to government-issued hardware only — private citizens and businesses can use DeepSeek freely."
    },
    {
      question: "How do I sign up for DeepSeek from the US?",
      answer: "Go to chat.deepseek.com, click 'Sign in' and register with your email, Google or Apple account. No phone number is required and sign-up is free. The interface auto-detects your locale and presents an English UI for US users."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://deepseek.ai/deepseek-usa",
        "url": "https://deepseek.ai/deepseek-usa",
        "name": "DeepSeek in the USA (2026): Access, Privacy Rules & API Pricing",
        "description": "Complete guide to DeepSeek in the United States: free chat access, CCPA privacy notes, US state bans, API pricing in USD and how it compares to ChatGPT.",
        "inLanguage": "en-US"
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqItems.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": { "@type": "Answer", "text": item.answer }
        }))
      }
    ]
  };

  return (
    <>
      <Head>
        <html lang="en-US" />
        <title>DeepSeek in the USA (2026): Access, Privacy Rules &amp; API Pricing</title>
        <meta name="description" content="DeepSeek in the USA: free chat access, CCPA privacy guidance, state-level bans, API pricing in USD and a comparison with ChatGPT. No VPN required." />
        <meta name="keywords" content="deepseek usa, deepseek america, is deepseek available in usa, deepseek ccpa, deepseek banned us states, deepseek api usa" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-usa" />
        <link rel="alternate" hrefLang="en-US" href="https://deepseek.ai/deepseek-usa" />
        <link rel="alternate" hrefLang="en" href="https://deepseek.ai/" />
        <link rel="alternate" hrefLang="x-default" href="https://deepseek.ai/" />
        <meta property="og:title" content="DeepSeek in the USA: Access, Privacy & API Pricing" />
        <meta property="og:description" content="Use DeepSeek in the United States: signup, CCPA, state bans, pricing in USD and US-specific FAQs." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-usa" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek in the USA: Access, Privacy & API Pricing" />
        <meta name="twitter:description" content="Use DeepSeek in the United States: signup, CCPA, state bans, pricing in USD and US-specific FAQs." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Head>

      <main className="min-h-screen bg-background">
        <section className="relative py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs currentPage="DeepSeek USA" />
            <div className="text-center mt-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <MapPin className="w-4 h-4 mr-2" /> Guide for American users
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                DeepSeek in the USA
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Everything Americans need to know — free chat access, CCPA privacy considerations, US state bans, API pricing in USD and how DeepSeek stacks up against ChatGPT.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://chat.deepseek.com" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90">
                  <MessageSquare className="w-5 h-5 mr-2" /> Open DeepSeek Chat <ExternalLink className="w-4 h-4 ml-2" />
                </a>
                <Link to="/deepseek-vs-chatgpt" className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-border bg-background font-semibold hover:bg-muted">
                  Compare to ChatGPT <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-6">Is DeepSeek available in the United States?</h2>
            <p className="text-lg text-muted-foreground mb-4">
              <strong className="text-foreground">Yes — DeepSeek works in every US state</strong>, from California to New York. No VPN is required. Both the consumer chat at chat.deepseek.com and the developer API at api.deepseek.com are accessible over standard US internet connections.
            </p>
            <p className="text-lg text-muted-foreground">
              There is no federal ban on DeepSeek for private use. However, several US states (Texas, New York, Virginia, Tennessee) and federal agencies (US Navy, NASA, Department of Commerce, Department of Defense) have restricted DeepSeek on government-issued devices over data-sovereignty concerns. These restrictions do not affect private citizens or commercial businesses.
            </p>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground text-center mb-4">DeepSeek API pricing in US dollars</h2>
            <p className="text-muted-foreground text-center mb-12">Native USD billing — no FX conversion required.</p>
            <div className="overflow-x-auto max-w-4xl mx-auto">
              <table className="w-full border-collapse bg-card border border-border rounded-xl overflow-hidden">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="py-4 px-6 text-left text-foreground font-semibold">Model</th>
                    <th className="py-4 px-6 text-left text-foreground font-semibold">Input (USD / 1M)</th>
                    <th className="py-4 px-6 text-left text-foreground font-semibold">Output (USD / 1M)</th>
                    <th className="py-4 px-6 text-left text-foreground font-semibold">Context</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border"><td className="py-4 px-6 font-semibold">DeepSeek-V4.1-Flash</td><td className="py-4 px-6 text-primary font-semibold">$0.15</td><td className="py-4 px-6">$0.60</td><td className="py-4 px-6">1M</td></tr>
                  <tr><td className="py-4 px-6 font-semibold">DeepSeek-V4-Pro</td><td className="py-4 px-6 text-primary font-semibold">$0.66</td><td className="py-4 px-6">$1.98</td><td className="py-4 px-6">1M</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-6">
              Input rates are cache-miss, off-peak prices. Peak hours (01:00–04:00 and 06:00–10:00 UTC, Monday to Friday) are billed at double these rates. The older <code>deepseek-chat</code> (V3) and <code>deepseek-reasoner</code> (R1) endpoints were switched off on 24 July 2026, so their rates no longer apply. Full breakdown on our <Link to="/pricing" className="text-primary hover:underline">pricing page</Link>.
            </p>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-4 p-6 rounded-xl bg-card border border-border">
              <Shield className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-3">CCPA and US privacy</h2>
                <p className="text-muted-foreground mb-3">
                  Under the <strong>California Consumer Privacy Act (CCPA/CPRA)</strong>, California residents have the right to know what personal information is collected, to delete it and to opt out of its sale. Because DeepSeek processes data on servers in China, enforcing those rights against the operator is practically difficult.
                </p>
                <p className="text-muted-foreground mb-3">
                  US businesses handling consumer data should:
                </p>
                <ul className="space-y-2 mb-3">
                  <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" />Obtain explicit consent before entering customer PII</li>
                  <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" />For HIPAA, GLBA or FedRAMP workloads, never use the hosted DeepSeek API</li>
                  <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" />Self-host DeepSeek-V3 on AWS us-east-1, Azure East US or Together AI for US data residency</li>
                </ul>
                <p className="text-muted-foreground">
                  For personal use, treat DeepSeek like any consumer chatbot — don't paste anything you wouldn't want stored. Full analysis on our{" "}
                  <Link to="/is-deepseek-safe" className="text-primary hover:underline">DeepSeek safety page</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Where DeepSeek is restricted in the US</h2>
            <p className="text-muted-foreground mb-8">
              Every restriction reported so far applies to government-issued devices and networks, not to private citizens or companies.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-card border border-border rounded-xl overflow-hidden">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="py-3 px-5 text-left text-foreground font-semibold">Jurisdiction</th>
                    <th className="py-3 px-5 text-left text-foreground font-semibold">Scope of restriction</th>
                    <th className="py-3 px-5 text-left text-foreground font-semibold">Applies to consumers?</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Texas", "State-owned devices and networks", "No"],
                    ["New York", "State government devices", "No"],
                    ["Virginia", "Executive-branch devices", "No"],
                    ["Tennessee", "State government devices", "No"],
                    ["US Navy / DoD", "Official hardware and networks", "No"],
                    ["NASA / Dept. of Commerce", "Agency devices", "No"],
                  ].map(([j, s, c]) => (
                    <tr key={j} className="border-b border-border last:border-0">
                      <td className="py-3 px-5 font-semibold text-foreground">{j}</td>
                      <td className="py-3 px-5 text-muted-foreground">{s}</td>
                      <td className="py-3 px-5 text-muted-foreground">{c}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              There is no federal ban on private or commercial use. Rules on government devices change frequently — check your own agency or state IT policy before installing anything.
            </p>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">US data residency: running DeepSeek models on American infrastructure</h2>
            <p className="text-muted-foreground mb-6">
              If your workload touches HIPAA health records, GLBA financial data or a FedRAMP boundary, the hosted DeepSeek API is the wrong choice — the request leaves US jurisdiction. Because the weights are open, you can keep the model and drop the offshore transfer:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">Self-host on a US region</strong> — AWS us-east-1 / us-west-2, Azure East US, or GCP us-central1 with vLLM or SGLang.</span></li>
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">US-based inference providers</strong> — Together AI, Fireworks and DeepInfra serve open DeepSeek weights from American data centres with signable data terms.</span></li>
              <li className="flex items-start text-muted-foreground"><Check className="w-4 h-4 text-primary mr-2 mt-1 flex-shrink-0" /><span><strong className="text-foreground">Note the trade-off</strong> — self-hosted routes run open weights, not the newest first-party V4-Pro checkpoint, so quality is a step behind the official API.</span></li>
            </ul>
            <p className="text-sm text-muted-foreground mt-6">
              Sizing your own GPUs? Our <Link to="/deepseek-api" className="text-primary hover:underline">API guide</Link> covers throughput, and the <Link to="/pricing" className="text-primary hover:underline">pricing page</Link> compares hosted cost against self-hosting.
            </p>
          </div>
        </section>

        <section className="py-16 bg-muted/30">

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">DeepSeek USA — FAQ</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border rounded-lg px-6">
                  <AccordionTrigger className="text-foreground font-semibold text-left hover:no-underline">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-20 bg-primary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">Try DeepSeek from the USA — free</h2>
            <p className="text-xl text-primary-foreground/90 mb-8">No credit card. No VPN. Works on any US connection.</p>
            <a href="https://chat.deepseek.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-white text-primary font-semibold hover:bg-gray-100">
              Open DeepSeek Chat <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default DeepseekUSA;
